// =============================================================================
// stores/r2ChatStore.ts  ·  R2 AI Chat Panel State
// Manages session, messages, panel state, and BIM object context
// =============================================================================

import { create } from 'zustand';

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  actions?: ActionSuggestion[];
  created_at: string;
  isStreaming?: boolean;
}

export interface ActionSuggestion {
  action: string;
  label: string;
}

export interface ChatContext {
  project_name?: string;
  building_name?: string;
  current_page?: string;
  selected_object?: Record<string, unknown> | null;
  open_rfis?: number;
  open_tasks?: number;
  recent_meetings?: number;
}

export interface ContextSummary {
  project_name: string;
  building: string;
  bim_objects: number;
  documents: number;
  rfis: number;
  meetings: number;
  open_tasks: number;
  understanding: Record<string, boolean>;
}

interface R2ChatState {
  // Panel state
  isOpen: boolean;
  isExpanded: boolean;
  isMinimized: boolean;

  // Session
  sessionId: string | null;

  // Messages
  messages: ChatMessage[];
  isStreaming: boolean;
  streamingMessageId: string | null;

  // Context
  context: ChatContext;
  contextSummary: ContextSummary | null;

  // Voice
  isListening: boolean;

  // Actions
  openPanel: () => void;
  closePanel: () => void;
  togglePanel: () => void;
  toggleExpand: () => void;
  setSessionId: (id: string) => void;
  addMessage: (msg: ChatMessage) => void;
  updateStreamingMessage: (id: string, delta: string) => void;
  finalizeMessage: (id: string, content: string, actions?: ActionSuggestion[]) => void;
  setStreaming: (val: boolean, msgId?: string) => void;
  updateContext: (ctx: Partial<ChatContext>) => void;
  setContextSummary: (cs: ContextSummary) => void;
  setListening: (val: boolean) => void;
  clearMessages: () => void;
}

export const useR2ChatStore = create<R2ChatState>((set, get) => ({
  isOpen:          false,
  isExpanded:      false,
  isMinimized:     false,
  sessionId:       null,
  messages:        [],
  isStreaming:     false,
  streamingMessageId: null,
  context:         { project_name: 'ABC Tower', building_name: '100 Storey Mixed Use Tower', current_page: 'Dashboard' },
  contextSummary:  null,
  isListening:     false,

  openPanel:        () => set({ isOpen: true, isMinimized: false }),
  closePanel:       () => set({ isOpen: false }),
  togglePanel:      () => set((s) => ({ isOpen: !s.isOpen, isMinimized: false })),
  toggleExpand:     () => set((s) => ({ isExpanded: !s.isExpanded })),
  setSessionId:     (id) => set({ sessionId: id }),
  addMessage:       (msg) => {
    if (msg.role === 'user') {
      try { window.dispatchEvent(new CustomEvent('r2:project-memory:ai-prompt', { detail: { prompt: msg.content } })); } catch { /* non-browser */ }
    }
    set((s) => ({ messages: [...s.messages, msg] }));
  },
  updateStreamingMessage: (id, delta) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, content: m.content + delta } : m
      ),
    })),
  finalizeMessage: (id, content, actions) =>
    set((s) => ({
      messages: s.messages.map((m) =>
        m.id === id ? { ...m, content, actions, isStreaming: false } : m
      ),
    })),
  setStreaming:     (val, msgId) => set({ isStreaming: val, streamingMessageId: msgId ?? null }),
  updateContext:    (ctx) => set((s) => ({ context: { ...s.context, ...ctx } })),
  setContextSummary: (cs) => set({ contextSummary: cs }),
  setListening:    (val) => set({ isListening: val }),
  clearMessages:   () => set({ messages: [] }),
}));
